import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import LanguageSelector from '../components/LanguageSelector';
import { Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = isLogin 
        ? await login(email, password)
        : await register(email, password);

      if (result.success) {
        toast.success(isLogin ? t('common.success') : t('common.success'));
        navigate('/');
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white relative">
        {/* Language Selector - Top Right */}
        <div className="absolute top-4 right-4">
          <LanguageSelector variant="compact" />
        </div>
        
        <div className="w-full max-w-md">
          <div className="mb-8">
            <img src="/logo.png" alt="Kaufmanns Consulting Logo" className="h-16 mb-4" />
            <h1 className="font-heading font-bold text-3xl tracking-tight text-slate-900 mb-2">
              Kaufmanns Consulting
            </h1>
            <p className="text-slate-600">Rechnungsverwaltungssystem</p>
          </div>

          <Card className="border-slate-200 shadow-none">
            <CardHeader>
              <CardTitle className="font-heading text-xl">
                {isLogin ? t('auth.loginTitle') : t('auth.registerTitle')}
              </CardTitle>
              <CardDescription>
                {isLogin 
                  ? t('auth.loginSubtitle')
                  : t('auth.registerSubtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="ihre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="email-input"
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="password-input"
                      className="rounded-sm pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-button"
                  className="w-full rounded-sm bg-primary hover:bg-[#D62839] shadow-none"
                >
                  {loading 
                    ? t('common.loading')
                    : isLogin ? t('auth.login') : t('auth.register')}
                </Button>
              </form>

              <div className="mt-4 text-center text-sm">
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  data-testid="toggle-mode-button"
                  className="text-primary hover:underline"
                >
                  {isLogin 
                    ? t('auth.noAccount') + ' ' + t('auth.register')
                    : t('auth.hasAccount') + ' ' + t('auth.login')}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Side - Image */}
      <div 
        className="hidden lg:block flex-1 bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1761887301783-096897b74171?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHJlZCUyMHdoaXRlJTIwbWluaW1hbGlzdCUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NjU2MzYyMjh8MA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="h-full bg-black/20 backdrop-blur-[1px]" />
      </div>
    </div>
  );
};

export default Login;